import { ErrorMessage } from '@hookform/error-message';
import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';

import { add, getAdjustedSuspense, subtract } from '@willow/shared-iso';
import {
  ControlledCurrencyInput,
  DATE_FORMAT_LONG,
  dateFormat,
  paymentAmountFormat,
  useZodHookForm,
  z,
} from '@willow/shared-web';

import { WillowModal } from '../../modal/Modal';

import './OverpaymentModal.scss';

interface Props {
  showModal: boolean;
  onClose: () => void;
  paymentAmount: number;
  amountDue: number;
  totalUpcomingAmountDue: number;
  bankName: string;
  onConfirm: (overrides: {
    additionalPrincipal: number;
    additionalEscrow: number;
    additionalSuspense: number;
  }) => Promise<void>;
}

const ADDITIONAL_PAYMENT_FORM_SHAPE = z
  .object({
    principal: z.string().transform(Number).or(z.number()),
    suspense: z.string().transform(Number).or(z.number()),
    overPaymentAmount: z.number(),
  })
  .superRefine((data, ctx) => {
    if (add(data.principal, data.suspense) !== data.overPaymentAmount) {
      ctx.addIssue({
        code: 'custom',
        message: `The recorded payment must equal ${paymentAmountFormat(data.overPaymentAmount)}`,
        path: ['principal'],
      });
      // Highlight both fields
      ctx.addIssue({
        code: 'custom',
        path: ['suspense'],
      });
    }
  });

type AdditionalPaymentFormShape = z.infer<typeof ADDITIONAL_PAYMENT_FORM_SHAPE>;

function getDefaultValues({
  paymentAmount,
  amountDue,
  totalUpcomingAmountDue,
}: {
  paymentAmount: number;
  amountDue: number;
  totalUpcomingAmountDue: number;
}) {
  const overPaymentAmount = subtract(paymentAmount, amountDue);
  const amountToSuspense = Math.min(subtract(totalUpcomingAmountDue, amountDue), overPaymentAmount);

  return {
    principal: subtract(overPaymentAmount, amountToSuspense),
    suspense: amountToSuspense,
    overPaymentAmount,
  };
}

export const OverpaymentModal = ({
  showModal,
  onClose,
  paymentAmount,
  amountDue,
  totalUpcomingAmountDue,
  bankName,
  onConfirm,
}: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const overPaymentAmount = subtract(paymentAmount, amountDue);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useZodHookForm(ADDITIONAL_PAYMENT_FORM_SHAPE, {
    defaultValues: getDefaultValues({ paymentAmount, amountDue, totalUpcomingAmountDue }),
  });

  useEffect(() => {
    if (showModal) {
      reset(getDefaultValues({ paymentAmount, amountDue, totalUpcomingAmountDue }));
    }
  }, [showModal, paymentAmount, amountDue, totalUpcomingAmountDue, reset]);

  const onSubmitAdditionalPayment: SubmitHandler<AdditionalPaymentFormShape> = async (data) => {
    try {
      setIsSubmitting(true);
      await onConfirm({
        additionalPrincipal: Number(data.principal),
        additionalEscrow: 0,
        additionalSuspense: getAdjustedSuspense(Number(data.suspense), totalUpcomingAmountDue, amountDue),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <WillowModal showModal={showModal} onClose={onClose}>
      <div className="payment-confirm-modal overpayment-modal">
        <h2>Overpayment Confirmation</h2>

        <div className="payment-confirm-modal__copy">
          <p>
            You have elected to pay <b>{paymentAmountFormat(overPaymentAmount)}</b> more than the contractual amount
            due. Please confirm where your overpayment should be applied.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmitAdditionalPayment)}>
          <div className="overpayment-modal__allocations">
            <div className="overpayment-modal__allocations__row">
              <label htmlFor="principal" className="overpayment-modal__allocations__label">
                Additional Principal
              </label>
              <div className="overpayment-modal__allocations__input">
                <ControlledCurrencyInput control={control} fieldName="principal" id="principal" hideErrorMessage />
              </div>
            </div>
            <div className="overpayment-modal__allocations__row">
              <label htmlFor="suspense" className="overpayment-modal__allocations__label">
                Future Statements
              </label>
              <div className="overpayment-modal__allocations__input">
                <ControlledCurrencyInput control={control} fieldName="suspense" id="suspense" hideErrorMessage />
              </div>
            </div>
            <ErrorMessage
              errors={errors}
              name="principal"
              render={({ message }) => <div className="u-fs-2 u-color-red1 mt-1">{message}</div>}
            />
          </div>

          <ul className="payment-confirm-modal__details">
            <li>
              <div className="payment-confirm-modal__details__title">Total amount</div>
              <div className="payment-confirm-modal__details__desc u-bold">{paymentAmountFormat(paymentAmount)}</div>
            </li>
            <li>
              <div className="payment-confirm-modal__details__title">From account</div>
              <div className="payment-confirm-modal__details__desc">{bankName}</div>
            </li>
            <li>
              <div className="payment-confirm-modal__details__title">Payment date</div>
              <div className="payment-confirm-modal__details__desc">{dateFormat(new Date(), DATE_FORMAT_LONG)}</div>
            </li>
          </ul>

          <div className="payment-confirm-modal__buttons">
            <button type="button" onClick={onClose} className="payment-confirm-modal__cancel-btn">
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className="payment-confirm-modal__submit-btn"
              data-ghost="portal-loan--modal-overpayment-confirm-button"
            >
              {isSubmitting ? 'Processing...' : 'Confirm Payment'}
            </button>
          </div>
        </form>
      </div>
    </WillowModal>
  );
};
